import { Input, Label, Modal as TwsModal } from '@aon/aon-tws-common-ui';
import * as React from 'react';
import { ChangeEvent, useEffect, useState } from 'react';
import HtmlParser from 'html-react-parser';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import Constants from '../../../constants';
import ContentConstants from '../../../contentConstants';
import { EmailTemplatePropertiesUnderValidation, IEmail, IEmailTemplate, IValidationErrorCollection } from '../../../models';
import { IApplicationState } from '../../../store';
import { ActionCreators as DetailsActionCreators } from '../../../store/AudienceDetailsStore';
import { ActionCreators } from '../../../store/EmailStore';
import { ValidationErrorHelper } from '../../../utilities/ValidationErrorHelper';
import { InstructionsContainer, InstructionsText } from '../../common';
import * as AudienceValidation from '../AudienceValidation';
import { SendEmailContainer } from '../SharedComponents';
import { ToastType } from '../ToastType';

const Modal = styled(TwsModal)`
	.modal-content {
		min-height: 500px;
	}
`;
Modal.displayName = 'Modal';

const SendEmailNote = styled.div`
	margin-top: 30px;
	font-size: 12px;
	color: #696969;
`;
SendEmailNote.displayName = 'SendEmailNote';

export interface ISendEmailModalProps {
	emailTemplate?: IEmailTemplate;
	audienceId?: number;
	surveyId?: number;
	modalOpen: boolean;
	shouldReplaceSurveyLink: boolean;
	closeModal: () => void;
}

export interface ISendEmailModalState {
	emailAddresses: string;
	emailTemplate?: IEmailTemplate;
	previewUrl?: string;
	validationError?: IValidationErrorCollection;
}

export const SendEmailModal = (props: ISendEmailModalProps) => {
	const startingState: ISendEmailModalState = {
		emailAddresses: '',
		emailTemplate: props.emailTemplate,
		validationError: undefined,
	};
	const [state, updateState] = useState({ ...startingState });
	const { invitationEmailTemplate, isSendingEmail, previewUrl } = useSelector((storeState: IApplicationState) => storeState.email);
	const dispatch = useDispatch();
	if (invitationEmailTemplate !== undefined && state.emailTemplate === undefined) {
		updateState({ ...state, emailTemplate: invitationEmailTemplate });
	}

	const { audienceId, shouldReplaceSurveyLink, surveyId } = props;
	useEffect(() => {
		if (audienceId && surveyId) {
			dispatch(ActionCreators.GetEmailTemplate(surveyId, audienceId));
		}
	}, [audienceId, surveyId, dispatch]);
	useEffect(() => {
		if (surveyId && shouldReplaceSurveyLink) {
			dispatch(ActionCreators.GetPreviewUrl(surveyId));
		}
	}, [surveyId, shouldReplaceSurveyLink, dispatch]);

	const { closeModal, modalOpen } = props;

	// When we are replacing the survey link token, we must have a previewUrl.
	const canSend = shouldReplaceSurveyLink ? !!previewUrl : true;
	const stateValidationErrorHelper = new ValidationErrorHelper<EmailTemplatePropertiesUnderValidation>(state.validationError);
	return (
		<Modal
			isOpen={modalOpen}
			title={ContentConstants.BUTTONS_SEND_TEST_EMAIL}
			primaryButtonText={'Send Email'}
			secondaryButtonText={'Cancel'}
			contenthcentered={true}
			contentvcentered={true}
			contentfullwidth={false}
			onClosed={onClose}
			onSubmit={sendEmail}
			type={'lightbox'}
			primaryButtonDisabled={isSendingEmail || !canSend}
		>
			<SendEmailContainer>
				<InstructionsContainer>
					<InstructionsText>{ContentConstants.INSTRUCTIONALTEXT_DISTRIBUTION_TEST_EMAIL_MULTI_EMAILS}</InstructionsText>
				</InstructionsContainer>
				<Label>{ContentConstants.EMAIL_ADDRESS}</Label>
				<Input
					placeholder={ContentConstants.INPUT_PLACEHOLDER_TEST_EMAILS}
					value={state.emailAddresses}
					error={!stateValidationErrorHelper.IsValid(EmailTemplatePropertiesUnderValidation.EmailAddresses)}
					message={stateValidationErrorHelper.Errors(EmailTemplatePropertiesUnderValidation.EmailAddresses)}
					onChange={onEmailAddressesChange}
				/>
				{!shouldReplaceSurveyLink && (
					<SendEmailNote>{HtmlParser(ContentConstants.INSTRUCTIONALTEXT_DISTRIBUTION_TEST_EMAIL_NOTE)}</SendEmailNote>
				)}
			</SendEmailContainer>
		</Modal>
	);

	function onEmailAddressesChange(event: ChangeEvent<HTMLInputElement>) {
		updateState({ ...state, emailAddresses: event.target.value });
	}

	function sendEmail() {
		const validationErrors = AudienceValidation.validEmailFields(state.emailAddresses);
		updateState({ ...state, validationError: validationErrors });
		if (validationErrors === undefined && state.emailTemplate) {
			const toEmailAddresses: string[] = state.emailAddresses.split(',');
			let template = { ...state.emailTemplate };
			if (shouldReplaceSurveyLink && previewUrl) {
				template = {
					...state.emailTemplate,
					content: state.emailTemplate.content.replace(Constants.SURVEY_LINK_PLACEHOLDER, previewUrl),
				};
			}
			const email: IEmail = {
				...template,
				toEmailAddresses,
			};
			dispatch(ActionCreators.SendEmail(email));
			onClose();
		} else {
			dispatch(DetailsActionCreators.Toast(ToastType.Warning, ContentConstants.TOAST_ERROR_GENERIC_BODY));
		}
	}

	function onClose() {
		updateState({ ...state, emailAddresses: '' });
		closeModal();
	}
};
